// Skriver storyboard-tilstanden direkte på DOM-elementerne hver frame, så
// React kun renderer når tekster ændrer sig.

import { OUT_FADE_SEC, baseState, evaluate, stateAfter, storyboardLength, type ElState, type StageState } from "./engine.js";
import type { GtSpec, GtStoryboardKey } from "./spec.js";

// Ubeskårne sider klippes et stykke uden for elementet, så skyggen bevares.
const SHADOW_ROOM = 60;

function clipPath(c: ElState["clip"]): string {
  if (c.every((v) => v <= 0)) return "none";
  return `inset(${c.map((v) => (v > 0 ? `${(v * 100).toFixed(3)}%` : `-${SHADOW_ROOM}px`)).join(" ")})`;
}

const fullyClipped = (c: ElState["clip"]) => c[0] + c[2] >= 1 || c[1] + c[3] >= 1;

interface Running {
  anims: Parameters<typeof evaluate>[1];
  start: StageState;
  t0: number;
  length: number;
  onDone?: () => void;
}

export class StagePlayer {
  // Synlighed bestemt af data (fx rundemarkører); vinder over storyboards.
  visibleOverrides: Record<string, boolean> = {};
  onAir = false;

  private nodes = new Map<string, HTMLElement>();
  private refs = new Map<string, (el: HTMLElement | null) => void>();
  private root: HTMLElement | null = null;
  private state: StageState;
  private running: Running | null = null;
  private fadeOut: { t0: number; onDone: () => void } | null = null;
  private rootOpacity = 1;
  private raf = 0;
  private fallback: ReturnType<typeof setTimeout> | undefined;

  constructor(readonly spec: GtSpec) {
    this.state = baseState(spec);
  }

  setRoot = (el: HTMLElement | null) => {
    this.root = el;
    if (el) this.apply();
  };

  ref(name: string) {
    let fn = this.refs.get(name);
    if (!fn) {
      fn = (el) => (el ? this.nodes.set(name, el) : this.nodes.delete(name));
      this.refs.set(name, fn);
    }
    return fn;
  }

  // Spring direkte til slutstillingen, fx når siden genindlæses mens grafikken er på.
  show(steps: string[]) {
    this.stop();
    this.state = stateAfter(this.spec, steps);
    this.onAir = true;
    this.rootOpacity = 1;
    this.apply();
  }

  play(key: GtStoryboardKey, onDone?: () => void) {
    const fromStart = key === "in";
    this.stop();
    if (fromStart) {
      this.state = baseState(this.spec);
      this.rootOpacity = 1;
    }
    this.onAir = true;
    // TransitionOut er skrevet som ind-animationer (Fly, Fade …); vMix spiller
    // dem som udgang, så de vendes her.
    const written = this.spec.storyboards[key];
    const anims = key === "out" ? written?.map((a) => ({ ...a, reverse: !a.reverse })) : written;
    this.running = { anims, start: this.state, t0: performance.now(), length: storyboardLength(anims), onDone };
    this.loop();
  }

  // Ud: grafikkens eget ud-storyboard, ellers vMix' fade af overlayet.
  playOut(onDone?: () => void) {
    if (!this.onAir) return;
    const finish = () => {
      this.hide();
      onDone?.();
    };
    if (this.spec.storyboards.out) {
      this.play("out", finish);
    } else {
      this.running = null;
      this.fadeOut = { t0: performance.now(), onDone: finish };
      this.loop();
    }
  }

  hide() {
    this.stop();
    this.onAir = false;
    this.apply();
  }

  private stop() {
    this.cancelFrame();
    this.running = null;
    this.fadeOut = null;
  }

  // requestAnimationFrame, med en timer som reserve hvis browseren drosler
  // frames (baggrundsfaner, headless) — så når animationen altid sin slutstilling.
  private loop() {
    if (this.raf) return;
    this.raf = requestAnimationFrame(this.tick);
    this.fallback = setTimeout(() => this.tick(performance.now()), 100);
  }

  private cancelFrame() {
    cancelAnimationFrame(this.raf);
    clearTimeout(this.fallback);
    this.raf = 0;
  }

  private tick = (now: number) => {
    this.cancelFrame();
    let busy = false;
    if (this.running) {
      const r = this.running;
      const t = (now - r.t0) / 1000;
      this.state = evaluate(this.spec, r.anims, r.start, Math.min(t, r.length));
      if (t >= r.length) {
        this.running = null;
        r.onDone?.();
      } else busy = true;
    }
    if (this.fadeOut) {
      const t = (now - this.fadeOut.t0) / 1000;
      this.rootOpacity = Math.max(0, 1 - t / OUT_FADE_SEC);
      if (t >= OUT_FADE_SEC) {
        const done = this.fadeOut.onDone;
        this.fadeOut = null;
        done();
      } else busy = true;
    }
    this.apply();
    if (busy) this.loop();
  };

  apply() {
    if (this.root) {
      // "" = arv fra forælderen, så en grafik inde i en anden (medical i UR)
      // også er skjult når den ydre er taget af.
      this.root.style.visibility = this.onAir ? "" : "hidden";
      this.root.style.opacity = String(this.rootOpacity);
    }
    for (const layer of this.spec.layers) {
      this.paint(layer.name, this.state[layer.name]!, 1);
      for (const el of layer.elements) {
        const s = this.state[el.name] ?? baseState(this.spec)[el.name]!;
        const visible = this.visibleOverrides[el.name] ?? s.visible;
        this.paint(el.name, { ...s, visible }, el.opacity, el.rotate, el.flipX);
      }
    }
  }

  private paint(name: string, s: ElState, opacity: number, rotate?: number, flipX?: boolean) {
    const node = this.nodes.get(name);
    if (!node) return;
    const shown = s.visible && s.fade > 0 && !fullyClipped(s.clip);
    node.style.visibility = shown ? "" : "hidden";
    node.style.opacity = String(opacity * s.fade);
    node.style.clipPath = clipPath(s.clip);
    // vMix roterer mod uret ved positiv vinkel; CSS med uret.
    node.style.transform =
      `translate(${s.dx}px, ${s.dy}px)` + (rotate ? ` rotate(${-rotate}deg)` : "") + (flipX ? " scaleX(-1)" : "");
  }
}
